import React, { useState, useEffect, useRef } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { 
  Container, 
  HeroSection, 
  Header, 
  Logo, 
  Nav, 
  Hamburger,
  MobileMenuOverlay,
  MobileMenu,
  GrabberHandle,
  MobileMenuLogo,
  Divider,
  MenuLink,
  HeroTitle, 
  ContentContainer, 
  Section, 
  SectionTitle, 
  Paragraph, 
  Footer 
} from "../styles/AboutPageStyles";
import logo from "../assets/logo-white.png";
import bgImage from '../assets/bgimagefinal.png';

function AboutPage() {
  const [menuOpen, setMenuOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const menuRef = useRef(null);

  // Debugging log to confirm component rendering
  useEffect(() => {
    console.log("AboutPage component rendered");
  }, []);

  const menuItems = [
    { label: "HOME", to: "/" }, // Removed active: true
    { label: "ABOUT", to: "/about" },
    { label: "CONTACT", to: "/contact" },
    { label: "LOGIN", to: "/login" },
  ];

  const toggleMenu = () => {
    setMenuOpen(!menuOpen);
  };

  const closeMenu = () => {
    setMenuOpen(false);
  };

  // Close menu when clicking outside or on grabber handle
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        closeMenu();
      }
    };

    if (menuOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    } else {
      document.removeEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [menuOpen]);

  // Handle ripple effect and vibration on link tap
  const handleLinkClick = (e, path) => {
    const link = e.currentTarget;
    const ripple = document.createElement("span");
    ripple.classList.add("ripple");
    const rect = link.getBoundingClientRect();
    const size = Math.max(rect.width, rect.height);
    ripple.style.width = ripple.style.height = `${size}px`;
    ripple.style.left = `${e.clientX - rect.left - size / 2}px`;
    ripple.style.top = `${e.clientY - rect.top - size / 2}px`;
    link.appendChild(ripple);

    // Vibration feedback (if supported)
    if ("vibrate" in navigator) {
      navigator.vibrate(50);
    }

    setTimeout(() => {
      closeMenu();
      navigate(path);
    }, 200);
  };

  // Determine active link based on current path
  const getLinkClass = (path) => {
    return location.pathname === path ? "active" : "";
  };

  return (
    <Container>
      <HeroSection bgImage={bgImage}>
        <Header>
          <Logo src={logo} alt="Nutrify Today" onError={() => console.error("Logo failed to load")} />
          <Nav>
            {menuItems.map((item) => (
              <Link
                key={item.label}
                to={item.to}
                className={`menu-item ${getLinkClass(item.to)}`} // Use getLinkClass for consistency
              >
                {item.label}
              </Link>
            ))}
            <Link to="/register" className={`menu-item register ${getLinkClass("/register")}`}>
              REGISTER
            </Link>
          </Nav>
          <Hamburger 
            className={menuOpen ? 'open' : ''} 
            onClick={toggleMenu}
            aria-label={menuOpen ? "Close menu" : "Open menu"}
            tabIndex={0}
            onKeyDown={(e) => e.key === "Enter" && toggleMenu()}
          >
            <span></span>
            <span></span>
            <span></span>
          </Hamburger>
        </Header>
        <HeroTitle>About Us</HeroTitle>
        <MobileMenuOverlay isOpen={menuOpen} onClick={closeMenu} />
        <MobileMenu isOpen={menuOpen} ref={menuRef}>
          <MobileMenuLogo src={logo} alt="Nutrify Today Logo" onError={() => console.error("Mobile menu logo failed to load")} />
          <Divider />
          {menuItems.map((item) => (
            <MenuLink 
              as={Link}
              key={item.label}
              to={item.to} 
              className={getLinkClass(item.to)} 
              onClick={(e) => handleLinkClick(e, item.to)} 
              tabIndex={0}
              onKeyDown={(e) => e.key === "Enter" && handleLinkClick(e, item.to)}
            >
              {item.label}
            </MenuLink>
          ))}
          <MenuLink 
            as={Link}
            to="/register" 
            className={`register ${getLinkClass("/register")}`} 
            onClick={(e) => handleLinkClick(e, "/register")} 
            tabIndex={0}
            onKeyDown={(e) => e.key === "Enter" && handleLinkClick(e, "/register")}
          >
            REGISTER
          </MenuLink>
          <GrabberHandle 
            onClick={closeMenu}
            aria-label="Close menu"
            tabIndex={0}
            onKeyDown={(e) => e.key === "Enter" && closeMenu()}
          />
        </MobileMenu>
      </HeroSection>

      <ContentContainer>
        <Section>
          <SectionTitle>Revolutionizing Nutraceuticals</SectionTitle>
          <Paragraph>
            Welcome to <strong>Deal Sphere</strong>, a cutting-edge platform designed to revolutionize the way businesses in the nutrition and health industries connect, collaborate, and grow. As part of the <strong>NutrifyToday</strong> ecosystem, Deal Sphere is a unique label claim selling web platform dedicated to providing businesses with the tools they need to thrive in the rapidly evolving world of nutraceuticals.
          </Paragraph>
          <Paragraph>
            At Deal Sphere, we empower companies in the responsible nutrition space by providing them with a platform to share, sell, and track product label claims. Our goal is to simplify the process of business development, allowing brands to showcase their unique label claims, connect with the right buyers, investors, and service providers. With Deal Sphere, businesses can expand their market reach, ensure regulatory compliance, and discover new opportunities for growth in a secure and transparent environment.
          </Paragraph>
        </Section>

        <Section>
          <SectionTitle>What We Do</SectionTitle>
          <Paragraph>
            Deal Sphere offers a streamlined platform where:
            <ul>
              <li>Nutraceutical brands can present and validate their product label claims to the market.</li>
              <li>Manufacturers and formulators can find new business leads and expand their client base.</li>
              <li>Investors can identify high-potential nutraceutical products and companies for potential funding and commercialization.</li>
              <li>Regulatory experts assist in ensuring label claims meet the latest industry standards, avoiding legal pitfalls and building market trust.</li>
              <li>Contract manufacturers, clinical trial agencies, and ingredient suppliers can connect directly with potential clients.</li>
            </ul>
            Through Deal Sphere, we aim to create a fair, transparent marketplace that fosters innovation and accelerates the commercialization process. By bridging the gap between diverse stakeholders, we contribute to the growth of the nutraceutical and responsible nutrition industries, creating a healthier world in the process.
          </Paragraph>
        </Section>

        <Section>
          <SectionTitle>Our Approach</SectionTitle>
          <Paragraph>
            We operate with a strong focus on curated market access and fast commercialization. Our platform ensures that label claims are credible, verifiable, and backed by scientific evidence. With continuous monitoring and a robust system for preventing malpractice, Deal Sphere ensures that businesses can confidently enter new markets while remaining compliant with regulatory guidelines.
          </Paragraph>
        </Section>

        <Section>
          <SectionTitle>Our Mission</SectionTitle>
          <Paragraph>
            At Deal Sphere, our mission is to transform innovation into business propositions through research, networking, market access, and integration. We are dedicated to assisting nutraceutical businesses at every stage, from product design to commercialization, while ensuring the highest standards of business and scientific ethics.
          </Paragraph>
        </Section>

        <Section>
          <SectionTitle>Why Choose Deal Sphere?</SectionTitle>
          <Paragraph>
            - <strong>Curated Market Access</strong>: Direct access to buyers, sellers, service providers, and investors within the nutraceutical and health food industries.
          </Paragraph>
          <Paragraph>
            - <strong>Networking Opportunities</strong>: Join a pre-screened, trusted group of professionals and organizations that share a passion for innovation in the health and wellness space.
          </Paragraph>
          <Paragraph>
            - <strong>Regulatory Compliance</strong>: Ensure your label claims are accurate and meet global regulatory standards.
          </Paragraph>
          <Paragraph>
            - <strong>Business Development Tools</strong>: Accelerate your product’s journey from concept to market with the support of a powerful ecosystem.
          </Paragraph>
          <Paragraph>
            - <strong>Transparent Marketplace</strong>: Continuous monitoring ensures all transactions and label claims are legitimate, fostering trust in the marketplace.
          </Paragraph>
        </Section>
      </ContentContainer>

      <Footer>
        <div>© 2025 DealSphere. All rights reserved.</div>
        <div>
          <a href="/terms">Terms</a>
          <a href="/privacy">Privacy</a>
          <a href="/contact">Contact</a>
        </div>
      </Footer>
    </Container>
  );
}

export default AboutPage;